/**
 * Category grouping for long facet dropdowns, derived from the values themselves: values that share
 * their trailing words ("Chocolate Protein Pudding", "Vanilla Protein Pudding") form a category named
 * by that shared tail, the more specific two-word tail winning over the one-word one. Values that
 * share a tail with nothing else gather under Other, last. Pure, so a brand's vocabulary needs no
 * curation to read well; a category needs at least two members to exist.
 */
import { COLOUR_FACET } from './colour.js';
import { FORMAT_RATIO_TOLERANCE, NAMED_PEOPLE_FACET } from './constants.js';
export const OTHER_GROUP_LABEL = 'Other';
const MIN_GROUP_SIZE = 2;
/** "puddings" and "pudding" are one tail; case and a plural s never split a category. */
function tailKey(words, n) {
    if (words.length < n + 1)
        return null; // a value that IS its tail ("Pudding") has no category of its own
    return words.slice(-n).map((w) => w.toLowerCase().replace(/s$/, '')).join(' ');
}
function labelFor(key) {
    const words = key.split(' ');
    const cased = words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w));
    const last = cased[cased.length - 1];
    cased[cased.length - 1] = last.endsWith('s') ? last : `${last}s`;
    return cased.join(' ');
}
const byLabel = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });
export function groupFacetValues(values) {
    const words = new Map(values.map((v) => [v, v.trim().split(/\s+/).filter(Boolean)]));
    const assigned = new Map();
    for (const n of [2, 1]) {
        const counts = new Map();
        for (const v of values) {
            if (assigned.has(v))
                continue;
            const key = tailKey(words.get(v), n);
            if (key)
                counts.set(key, (counts.get(key) ?? 0) + 1);
        }
        for (const v of values) {
            if (assigned.has(v))
                continue;
            const key = tailKey(words.get(v), n);
            if (key && (counts.get(key) ?? 0) >= MIN_GROUP_SIZE)
                assigned.set(v, key);
        }
    }
    const groups = new Map();
    const other = [];
    for (const v of values) {
        const key = assigned.get(v);
        if (!key)
            other.push(v);
        else
            groups.set(key, [...(groups.get(key) ?? []), v]);
    }
    const out = [...groups.entries()]
        .map(([key, vs]) => ({ label: labelFor(key), values: [...vs].sort(byLabel) }))
        .sort((a, b) => b.values.length - a.values.length || byLabel(a.label, b.label));
    if (other.length)
        out.push({ label: OTHER_GROUP_LABEL, values: [...other].sort(byLabel) });
    return out;
}
/**
 * Whether a category holds one value per clip (Dax, 2026-10-01): the people category (one person on
 * camera) and the format category (one shape, read off the pixels). Every other category, whatever a
 * brand calls it (flavours, colours, benefits, styles, services), may hold several at once.
 */
export function isSingleChoiceCategory(key, values) {
    return key === NAMED_PEOPLE_FACET || isFormatCategory(values);
}
/**
 * The fields of the tag modal the user actually changed. `initial` is what every listed clip shared
 * when the modal opened, `chosen` what each field holds on save; a field is a list of values (one at
 * most for a single-choice category). Only the difference goes: what was ticked is added to every clip,
 * what was unticked is taken off every clip, and a value only some clips carried, never shown, is left
 * alone. Untouched fields are absent. Case and surrounding spaces never count as a change.
 */
export function facetChanges(initial, chosen) {
    const clean = (list) => [...new Map((list ?? []).map((v) => v.trim()).filter(Boolean).map((v) => [v.toLowerCase(), v])).values()];
    const has = (list, v) => list.some((x) => x.toLowerCase() === v.toLowerCase());
    const out = [];
    for (const key of new Set([...Object.keys(initial), ...Object.keys(chosen)])) {
        const before = clean(initial[key]);
        const after = clean(chosen[key]);
        const add = after.filter((v) => !has(before, v));
        const remove = before.filter((v) => !has(after, v));
        if (add.length || remove.length)
            out.push({ key, add, remove });
    }
    return out;
}
/** The ratio a value's label names, width over height: "wide 16:9", "upright 9x16", "square 1:1". */
export function labelRatio(label) {
    const m = /(\d+(?:\.\d+)?)\s*[:x×]\s*(\d+(?:\.\d+)?)/i.exec(label);
    if (!m)
        return null;
    const w = Number(m[1]);
    const h = Number(m[2]);
    return w > 0 && h > 0 ? w / h : null;
}
/** The format category is the one whose values name ratios; its value is read from the pixels, never asked of the AI. */
export function isFormatCategory(values) {
    return values.some((v) => labelRatio(v.label) !== null);
}
/**
 * The value naming a clip's displayed shape: the closest ratio within FORMAT_RATIO_TOLERANCE, else none.
 * A 2160x3840 phone clip is "upright 9:16"; a 1080x1350 one is "instagram 4:5", not the nearer-sounding 9:16.
 */
export function formatValueForShape(values, width, height) {
    if (!width || !height)
        return null;
    const clip = width / height;
    let best = null;
    for (const value of values) {
        const r = labelRatio(value.label);
        if (r === null)
            continue;
        const off = Math.abs(clip / r - 1);
        if (off <= FORMAT_RATIO_TOLERANCE && (!best || off < best.off))
            best = { value, off };
    }
    return best?.value ?? null;
}
/**
 * Tags about the sound rather than the picture: the analysis's "audio · voice" and the "says · …" lines a
 * spoken phrase becomes. The tagging page keeps them on their own tab.
 */
export const AUDIO_TAG_KEYS = ['audio', 'says'];
export function isAudioTag(label) {
    const key = label.split(' · ')[0]?.trim().toLowerCase() ?? '';
    return AUDIO_TAG_KEYS.includes(key);
}
/** How many of a brand's categories the Library's line under a clip's name summarises. */
export const LIBRARY_SUMMARY_FACETS = 2;
/**
 * The categories the Library summarises under a clip's name: the brand's own first ones in its settings
 * order, whatever it sells, skipping the ones read off the pixels (format, colour) and the people, who
 * have their own column. Every brand's first categories are the ones it put first.
 */
export function summaryFacetIds(facets) {
    return facets
        .filter((f) => f.key !== NAMED_PEOPLE_FACET && f.key !== COLOUR_FACET && !isFormatCategory(f.values))
        .slice(0, LIBRARY_SUMMARY_FACETS)
        .map((f) => f.id);
}
//# sourceMappingURL=facets.js.map